import { useState } from "react";
import { Loader2, LogIn, Sparkles, UserPlus } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const field = "h-12 border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold";
const TABS = [["signin", "Sign in", LogIn], ["register", "Create account", UserPlus], ["link", "Email me a link", Sparkles]];

/** Passenger sign-in: email + password, with the emailed private link as the fallback. */
export const RiderAuthPanel = ({ companyId, onSignedIn }) => {
  const [tab, setTab] = useState("signin");
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const go = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      if (tab === "link") {
        await api.post(`/public/company/${companyId}/rider-link`, { email: form.email });
        setNotice("Check your inbox — we sent you a private link to your trips.");
      } else {
        const path = tab === "register" ? "register" : "login";
        const { data } = await api.post(`/public/rider-auth/${companyId}/${path}`, form);
        onSignedIn(data);
      }
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="rider-auth-panel" className="rounded-xl border border-ink-600 bg-ink-800/60 p-5">
      <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Your trips</p>
      <p className="mt-2 text-xs text-slate-500">
        Sign in to track a ride, add a stop, reschedule and book faster with saved addresses.
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        {TABS.map(([k, label, Icon]) => (
          <button
            type="button"
            key={k}
            data-testid={`rider-auth-tab-${k}`}
            onClick={() => { setTab(k); setError(""); setNotice(""); }}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
              tab === k ? "border-gold/50 bg-gold/10 text-gold" : "border-ink-600 text-slate-400 hover:border-gold/30"
            }`}
          >
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {tab === "register" && (
          <div className="grid gap-2 sm:grid-cols-2">
            <Input data-testid="rider-auth-name" placeholder="Your name" value={form.name} onChange={set("name")} className={field} />
            <Input data-testid="rider-auth-phone" placeholder="Mobile number" value={form.phone} onChange={set("phone")} className={field} />
          </div>
        )}
        <Input data-testid="rider-auth-email" type="email" placeholder="Email address" value={form.email}
          onChange={set("email")} className={field} />
        {tab !== "link" && (
          <Input data-testid="rider-auth-password" type="password" autoComplete={tab === "register" ? "new-password" : "current-password"}
            placeholder={tab === "register" ? "Choose a password (8+ characters)" : "Password"}
            value={form.password} onChange={set("password")} className={field} />
        )}
        <Button data-testid="rider-auth-submit" disabled={busy || !form.email} onClick={go}
          className="h-12 w-full bg-gold text-ink-900 hover:bg-gold-light">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" />
            : tab === "register" ? "Create my account" : tab === "link" ? "Email my link" : "Sign in"}
        </Button>
        {tab === "signin" && (
          <p className="text-[11px] text-slate-500">
            Forgot your password? Use <span className="text-gold">Email me a link</span> — you can set a new one from there.
          </p>
        )}
        {error && <p data-testid="rider-auth-error" className="text-sm text-red-400">{error}</p>}
        {notice && <p data-testid="rider-auth-notice" className="text-sm text-emerald-400">{notice}</p>}
      </div>
    </div>
  );
};
